import { useCallback, useMemo } from 'react'

export interface SelectableItem {
  properties: {
    id?: string | number
    tmpId?: string
    selected?: boolean
    [key: string]: any
  }
  [key: string]: any
}

export interface UseAccordionSelectionProps<T extends SelectableItem> {
  items: T[]
  onItemsChange: (items: T[]) => void
  getItemId: (item: T) => string
}

export interface UseAccordionSelectionReturn {
  selectedItems: Set<string>
  selectAll: boolean
  handleSelectAll: (checked: boolean) => void
  handleItemSelection: (itemId: string, selected: boolean) => void
  isItemSelected: (itemId: string) => boolean
}

export const useAccordionSelection = <T extends SelectableItem>({
  items,
  onItemsChange,
  getItemId,
}: UseAccordionSelectionProps<T>): UseAccordionSelectionReturn => {
  const selectedItems = useMemo(() => {
    const selected = new Set<string>()
    items.forEach(item => {
      if (item.properties?.selected) {
        selected.add(getItemId(item))
      }
    })
    return selected
  }, [items, getItemId])

  const selectAll = useMemo(
    () => items.length > 0 && selectedItems.size === items.length,
    [items.length, selectedItems.size],
  )

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Update items with selected property
        const updatedItems = items.map(item => ({
          ...item,
          properties: { ...item.properties, selected: true },
        }))
        onItemsChange(updatedItems)
      } else {
        // Remove selected property from all items
        const updatedItems = items.map(item => ({
          ...item,
          properties: { ...item.properties, selected: false },
        }))
        onItemsChange(updatedItems)
      }
    },
    [items, onItemsChange],
  )

  const handleItemSelection = useCallback(
    (itemId: string, selected: boolean) => {
      // Update the specific item's selected property
      const updatedItems = items.map(item => {
        const currentItemId = getItemId(item)
        if (currentItemId === itemId) {
          return {
            ...item,
            properties: { ...item.properties, selected },
          }
        }
        return item
      })
      onItemsChange(updatedItems)
    },
    [items, onItemsChange, getItemId],
  )

  const isItemSelected = useCallback((itemId: string) => selectedItems.has(itemId), [selectedItems])

  return {
    selectedItems,
    selectAll,
    handleSelectAll,
    handleItemSelection,
    isItemSelected,
  }
}
